import { useQuery, type QueryClient } from '@tanstack/react-query';
import { fetchDocumentBlob, fetchDocumentThumbnail } from '@apis/documents';

/**
 * One download per file, shared by every surface that renders its bytes.
 * Cached forever, because a file's bytes never change — but its id is a PATH,
 * which a delete/rename/move frees for the next upload to reuse. Those call
 * sites must call `evictDocumentBlobs`.
 */
export const documentBlobQueryKey = (siteId: string, fileId: string) =>
  ['documents', 'blob', siteId, fileId] as const;

/** How long an unused blob stays in memory. Bounds the cache: a handful of
 *  recently viewed files, not everything ever opened. */
const BLOB_GC_MS = 10 * 60 * 1000;

export function useDocumentBlob(siteId: string, fileId: string, enabled = true) {
  return useQuery({
    queryKey: documentBlobQueryKey(siteId, fileId),
    queryFn: async ({ signal }) => (await fetchDocumentBlob(siteId, fileId, signal)).blob,
    enabled,
    staleTime: Infinity,
    gcTime: BLOB_GC_MS,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/** Nested under the blob key, so evicting a file's bytes drops its thumbnail too. */
export const documentThumbnailQueryKey = (siteId: string, fileId: string) =>
  [...documentBlobQueryKey(siteId, fileId), 'thumbnail'] as const;

/**
 * The file's preview image. `null` means there is none — fall back to
 * `useDocumentBlob`. The markup editor must keep using `useDocumentBlob`: it
 * bakes into the full-size bytes, which previews no longer warm.
 */
export function useDocumentThumbnail(siteId: string, fileId: string, enabled = true) {
  return useQuery({
    queryKey: documentThumbnailQueryKey(siteId, fileId),
    queryFn: ({ signal }) => fetchDocumentThumbnail(siteId, fileId, signal),
    enabled,
    staleTime: Infinity,
    gcTime: BLOB_GC_MS,
    refetchOnWindowFocus: false,
    // A failure falls back to the full bytes at once; that download retries.
    retry: false,
  });
}

/**
 * Drops cached bytes and thumbnails for every file under a folder. For folder
 * writes: a rename or move re-keys every file under it, a delete takes them
 * with it. Ids are paths, so the folder's files are found without its listing.
 */
export function evictFolderBlobs(qc: QueryClient, siteId: string, folderId: string): void {
  qc.removeQueries({
    queryKey: ['documents', 'blob', siteId],
    predicate: (query) => String(query.queryKey[3]).startsWith(`${folderId}/`),
  });
}

/** Drops cached bytes and thumbnails for ids that no longer address the file they came from. */
export function evictDocumentBlobs(qc: QueryClient, siteId: string, fileIds: readonly string[]): void {
  for (const fileId of fileIds) {
    qc.removeQueries({ queryKey: documentBlobQueryKey(siteId, fileId) });
  }
}

/** Carries a file's cached bytes and thumbnail over to the id it was re-keyed
 *  to: the same file under a new path, so nothing needs downloading again. */
export function moveDocumentBlobs(qc: QueryClient, siteId: string, fromId: string, toId: string): void {
  const from = documentBlobQueryKey(siteId, fromId);
  for (const [key, data] of qc.getQueriesData({ queryKey: from })) {
    qc.setQueryData([...documentBlobQueryKey(siteId, toId), ...key.slice(from.length)], data);
  }
  evictDocumentBlobs(qc, siteId, [fromId]);
}

/** Every fileId a document owns: the row's id, plus one per version. */
export function documentFileIds(doc: { id: string; versions: { fileId: string }[] }): string[] {
  return [doc.id, ...doc.versions.map((v) => v.fileId)];
}

/** Warms the cache ahead of an expected open (e.g. the markup editor). */
export function prefetchDocumentBlob(qc: QueryClient, siteId: string, fileId: string): void {
  void qc.prefetchQuery({
    queryKey: documentBlobQueryKey(siteId, fileId),
    queryFn: async ({ signal }) => (await fetchDocumentBlob(siteId, fileId, signal)).blob,
    staleTime: Infinity,
    gcTime: BLOB_GC_MS,
  });
}
